import type { Contact } from './types.js';

/**
 * ARCHITECTURAL INVARIANT
 * ───────────────────────
 * Server filters are a strict *superset* of what a lane renders. This module
 * remains the sole authority on lane membership, due-date bucketing, and what
 * counts as "active". Anything pushed down to Supabase/PostgREST (see
 * `LANE_QUERY_SPEC` below, and its use in `src/lib/crm/api.ts`) only reduces
 * payload size — it must never be relied on to be exact, and it must never
 * be tightened using a filter that could silently drop rows a NULL column
 * value should still match (e.g. `action_state=not.in.(...)` excludes NULL
 * action_state under PostgREST's three-valued logic). Do not move lane or
 * bucket logic into SQL. Do not "optimise" by trusting a server filter to be
 * exact.
 */

export type DashboardFilter =
  | null
  | 'overdue'
  | 'due-today'
  | 'due-this-week'
  | 'total-active';

export type DashboardLane =
  | 'my-turn'
  | 'their-turn'
  | 'scheduled'
  | 'nurture'
  | 'on-hold';

/**
 * The minimal shape every lane/bucket classifier actually reads. `Contact`
 * satisfies this structurally (its optional fields are `string | undefined`,
 * a subset of `string | null | undefined`), and so does the lightweight
 * `ContactIndexRow` DB projection below — one classification ladder serves
 * both full contact rows and the 6-column index projection.
 */
interface DashboardContactSnapshot {
  status: string;
  action_state?: string | null;
  due_date?: string | null;
  last_contact_date?: string | null;
  completed_at?: string | null;
}

/**
 * The 6-column Supabase projection used for dashboard counts, so a badge/
 * counter refresh never has to pull full contact rows (name, notes, ai
 * metadata, ...) over the wire.
 */
export interface ContactIndexRow {
  id: string;
  status: string;
  action_state: string | null;
  due_date: string | null;
  last_contact_date: string | null;
  completed_at: string | null;
}

export interface DashboardSectionGroups {
  myTurn: {
    overdue: Contact[];
    today: Contact[];
    thisWeek: Contact[];
    later: Contact[];
    total: number;
  };
  theirTurn: {
    fresh: Contact[];
    stale: Contact[];
    followUp: Contact[];
    total: number;
  };
  scheduled: Contact[];
  nurture: Contact[];
  onHold: Contact[];
}

export interface DashboardStatsModel {
  overdue: number;
  dueToday: number;
  dueThisWeek: number;
  totalActive: number;
}

export interface DashboardModel {
  stats: DashboardStatsModel;
  sections: DashboardSectionGroups;
  hasActiveContacts: boolean;
}

type DueBucket = 'overdue' | 'today' | 'thisWeek' | 'later';
type WaitingBucket = 'fresh' | 'stale' | 'followUp';

const INACTIVE_ACTION_STATES = new Set([
  'archived',
  'closed',
  'closed_won',
  'closed_lost',
]);

const INACTIVE_STATUSES = new Set([
  'archived',
  'closed',
  'closed_won',
  'closed_lost',
]);

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const parsed = dateOnlyMatch
    ? new Date(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3]),
      )
    : new Date(value);

  if (
    dateOnlyMatch
    && (
      parsed.getFullYear() !== Number(dateOnlyMatch[1])
      || parsed.getMonth() !== Number(dateOnlyMatch[2]) - 1
      || parsed.getDate() !== Number(dateOnlyMatch[3])
    )
  ) {
    return null;
  }

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(value: Date): Date {
  const result = new Date(value);
  result.setHours(0, 0, 0, 0);
  return result;
}

function getDueBucket(contact: DashboardContactSnapshot, now: Date): DueBucket {
  const dueDate = parseDate(contact.due_date);
  if (!dueDate) return 'later';

  const today = startOfDay(now);
  const due = startOfDay(dueDate);
  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + ((7 - today.getDay()) % 7));

  if (due < today) return 'overdue';
  if (due.getTime() === today.getTime()) return 'today';
  if (due <= endOfWeek) return 'thisWeek';
  return 'later';
}

function getWaitingBucket(contact: DashboardContactSnapshot, now: Date): WaitingBucket {
  const lastContact = parseDate(contact.last_contact_date);
  if (!lastContact) return 'followUp';

  const elapsedDays = Math.floor(
    (startOfDay(now).getTime() - startOfDay(lastContact).getTime()) / 86_400_000,
  );

  if (elapsedDays < 3) return 'fresh';
  if (elapsedDays <= 7) return 'stale';
  return 'followUp';
}

function contactSortTimestamp(contact: Contact): number {
  return (
    parseDate(contact.due_date)?.getTime()
    ?? parseDate(contact.last_contact_date)?.getTime()
    ?? Number.POSITIVE_INFINITY
  );
}

function sortContacts(contacts: Contact[]): Contact[] {
  return [...contacts].sort((left, right) => {
    const leftTimestamp = contactSortTimestamp(left);
    const rightTimestamp = contactSortTimestamp(right);
    if (leftTimestamp !== rightTimestamp) {
      return leftTimestamp < rightTimestamp ? -1 : 1;
    }

    const leftCreatedAt =
      parseDate(left.created_at)?.getTime() ?? Number.POSITIVE_INFINITY;
    const rightCreatedAt =
      parseDate(right.created_at)?.getTime() ?? Number.POSITIVE_INFINITY;
    if (leftCreatedAt !== rightCreatedAt) {
      return leftCreatedAt < rightCreatedAt ? -1 : 1;
    }

    const nameDifference = left.name.localeCompare(right.name, 'en-US');
    return nameDifference !== 0 ? nameDifference : left.id.localeCompare(right.id);
  });
}

export function isActiveDashboardContact(contact: DashboardContactSnapshot): boolean {
  if (contact.completed_at) return false;

  const actionState = contact.action_state?.toLowerCase();
  const status = contact.status?.toLowerCase();

  return !(
    (actionState && INACTIVE_ACTION_STATES.has(actionState))
    || (status && INACTIVE_STATUSES.has(status))
  );
}

/**
 * The single branch ladder that decides which of the 5 dashboard lanes an
 * active contact belongs to (or `null` for an inactive one). Both
 * `partitionDashboardContacts` (full contact rows, for rendering) and
 * `createDashboardCounts` (the lightweight `ContactIndexRow` projection, for
 * badge counts) call this — there is exactly one ladder.
 *
 * `now` is accepted for signature symmetry with the sibling bucket helpers
 * (`getDueBucket`, `getWaitingBucket`, `matchesDashboardFilter`), all of
 * which are also called with an explicit clock, and to leave room for a
 * future date-based lane rule without a signature change. The current
 * ladder is not itself time-dependent.
 */
export function getDashboardLane(
  contact: DashboardContactSnapshot,
  now: Date,
): DashboardLane | null {
  void now;

  if (!isActiveDashboardContact(contact)) return null;

  if (contact.action_state === 'on_hold') return 'on-hold';
  if (contact.action_state === 'scheduled') return 'scheduled';
  if (contact.action_state === 'waiting_response') return 'their-turn';
  if (contact.status === 'nurture' && !contact.action_state) return 'nurture';

  return 'my-turn';
}

/**
 * Declarative, Supabase-free description of a server-side filter that is a
 * SAFE SUPERSET of the contacts `getDashboardLane` assigns to a given lane —
 * see the architectural invariant at the top of this file. Consumed by
 * `src/lib/crm/api.ts` to narrow `listContacts`'s `lane` option; never
 * consumed here.
 *
 * Matching semantics (a contact matches a spec when ALL specified
 * constraints hold; an omitted constraint imposes no restriction):
 *   - `statusIn`: `status` must be one of these values.
 *   - `actionStateIn` / `allowNullActionState`: `action_state` must be one of
 *     `actionStateIn`, or — if `allowNullActionState` is true — absent/null.
 *     Deliberately never expressed as `not.in`: PostgREST's `NOT (col IN
 *     (...))` is NULL (i.e. excluded) for a NULL `action_state`, which would
 *     silently drop exactly the null-action_state rows this spec must keep.
 */
export interface LaneQuerySpec {
  actionStateIn?: string[];
  allowNullActionState?: boolean;
  statusIn?: string[];
}

export const LANE_QUERY_SPEC: Record<DashboardLane, LaneQuerySpec> = {
  'on-hold': { actionStateIn: ['on_hold'] },
  scheduled: { actionStateIn: ['scheduled'] },
  'their-turn': { actionStateIn: ['waiting_response'] },
  nurture: { statusIn: ['nurture'], allowNullActionState: true },
  // Everything the ladder doesn't route elsewhere: action_required or
  // response_received outright, or a null action_state on a non-nurture
  // status (on_hold/scheduled/waiting_response contacts never reach this
  // branch — they're claimed by the three lanes above first).
  'my-turn': {
    actionStateIn: ['action_required', 'response_received'],
    allowNullActionState: true,
  },
};

export function partitionDashboardContacts(
  contacts: Contact[],
  now: Date,
  options: { preserveOrder?: boolean } = {},
): DashboardSectionGroups {
  const preserveOrder = options.preserveOrder ?? false;

  const myTurn = {
    overdue: [] as Contact[],
    today: [] as Contact[],
    thisWeek: [] as Contact[],
    later: [] as Contact[],
    total: 0,
  };
  const theirTurn = {
    fresh: [] as Contact[],
    stale: [] as Contact[],
    followUp: [] as Contact[],
    total: 0,
  };
  const scheduled: Contact[] = [];
  const nurture: Contact[] = [];
  const onHold: Contact[] = [];

  for (const contact of contacts) {
    const lane = getDashboardLane(contact, now);
    if (lane === null) continue;

    if (lane === 'on-hold') {
      onHold.push(contact);
      continue;
    }

    if (lane === 'scheduled') {
      scheduled.push(contact);
      continue;
    }

    if (lane === 'their-turn') {
      theirTurn[getWaitingBucket(contact, now)].push(contact);
      continue;
    }

    if (lane === 'nurture') {
      nurture.push(contact);
      continue;
    }

    myTurn[getDueBucket(contact, now)].push(contact);
  }

  // Paged/server-ordered callers pass `preserveOrder: true` to trust the
  // server's ORDER BY — re-sorting inside a page would contradict the
  // server's page boundaries and make rows visibly jump between pages.
  const finalizeOrder = (list: Contact[]): Contact[] =>
    (preserveOrder ? list : sortContacts(list));

  myTurn.overdue = finalizeOrder(myTurn.overdue);
  myTurn.today = finalizeOrder(myTurn.today);
  myTurn.thisWeek = finalizeOrder(myTurn.thisWeek);
  myTurn.later = finalizeOrder(myTurn.later);
  myTurn.total =
    myTurn.overdue.length
    + myTurn.today.length
    + myTurn.thisWeek.length
    + myTurn.later.length;

  theirTurn.fresh = finalizeOrder(theirTurn.fresh);
  theirTurn.stale = finalizeOrder(theirTurn.stale);
  theirTurn.followUp = finalizeOrder(theirTurn.followUp);
  theirTurn.total =
    theirTurn.fresh.length
    + theirTurn.stale.length
    + theirTurn.followUp.length;

  return {
    myTurn,
    theirTurn,
    scheduled: finalizeOrder(scheduled),
    nurture: finalizeOrder(nurture),
    onHold: finalizeOrder(onHold),
  };
}

function matchesDashboardFilter(
  contact: Contact,
  filter: DashboardFilter,
  now: Date,
): boolean {
  if (!filter || filter === 'total-active') return true;

  const bucket = getDueBucket(contact, now);
  if (filter === 'overdue') return bucket === 'overdue';
  if (filter === 'due-today') return bucket === 'today';
  return bucket === 'today' || bucket === 'thisWeek';
}

export function createDashboardModel(
  contacts: Contact[],
  filter: DashboardFilter,
  now: Date,
): DashboardModel {
  const activeContacts = contacts.filter(isActiveDashboardContact);
  const stats: DashboardStatsModel = {
    overdue: activeContacts.filter((contact) => getDueBucket(contact, now) === 'overdue').length,
    dueToday: activeContacts.filter((contact) => getDueBucket(contact, now) === 'today').length,
    dueThisWeek: activeContacts.filter((contact) => {
      const bucket = getDueBucket(contact, now);
      return bucket === 'today' || bucket === 'thisWeek';
    }).length,
    totalActive: activeContacts.length,
  };
  const visibleContacts = activeContacts.filter((contact) =>
    matchesDashboardFilter(contact, filter, now));

  return {
    stats,
    sections: partitionDashboardContacts(visibleContacts, now),
    hasActiveContacts: activeContacts.length > 0,
  };
}

export function countDashboardSectionContacts(sections: DashboardSectionGroups): number {
  return (
    sections.myTurn.total
    + sections.theirTurn.total
    + sections.scheduled.length
    + sections.nurture.length
    + sections.onHold.length
  );
}

export interface DashboardCounts {
  stats: DashboardStatsModel;
  laneCounts: Record<DashboardLane, number>;
  hasActiveContacts: boolean;
}

/**
 * Badge/counter-only variant of `createDashboardModel`: same due-bucket math
 * and the same lane ladder (via `getDashboardLane`), but over the 6-column
 * `ContactIndexRow` projection instead of full `Contact` rows, and without
 * building the row lists themselves. Intended for a cheap, frequently-
 * refetched counts query that doesn't need to pull every contact field.
 */
export function createDashboardCounts(
  index: ContactIndexRow[],
  now: Date,
): DashboardCounts {
  const activeRows = index.filter(isActiveDashboardContact);

  const stats: DashboardStatsModel = {
    overdue: activeRows.filter((row) => getDueBucket(row, now) === 'overdue').length,
    dueToday: activeRows.filter((row) => getDueBucket(row, now) === 'today').length,
    dueThisWeek: activeRows.filter((row) => {
      const bucket = getDueBucket(row, now);
      return bucket === 'today' || bucket === 'thisWeek';
    }).length,
    totalActive: activeRows.length,
  };

  const laneCounts: Record<DashboardLane, number> = {
    'my-turn': 0,
    'their-turn': 0,
    scheduled: 0,
    nurture: 0,
    'on-hold': 0,
  };

  for (const row of activeRows) {
    const lane = getDashboardLane(row, now);
    if (lane) laneCounts[lane] += 1;
  }

  return {
    stats,
    laneCounts,
    hasActiveContacts: activeRows.length > 0,
  };
}
